/**
 * Type Definitions for API Error Handling
 *
 * These types define error structures returned from the backend API.
 */

/**
 * API error response structure from backend
 */
export interface APIError {
  error: string; // Error type: "Validation Error", "Not Found", "Conflict", etc.
  message: string; // Human-readable error message
  details?: Record<string, any>; // Field-name → message map, used by v3 form screens
  detail?: unknown; // The backend `detail` verbatim, whatever shape it came in
}

/**
 * One problem reported by a backend validator, e.g. taxonomy draft validation.
 *
 * `message` says what is wrong and `hint` says how to fix it. `hint` is optional
 * so that a frontend deployed ahead of the API degrades to message-only rather
 * than rendering an empty row.
 */
export interface StructuredValidationIssue {
  path: string;
  message: string;
  rule: string;
  hint?: string;
}

export interface StructuredValidationDetail {
  message: string;
  errors: StructuredValidationIssue[];
  warnings?: StructuredValidationIssue[];
}

/**
 * Narrow an unknown error to one carrying a structured validation detail.
 *
 * Lives here rather than in a feature folder because the shape is a transport
 * concern: more than one endpoint returns it, and this module owns `APIError`.
 */
export const isStructuredValidationError = (
  error: unknown,
): error is APIErrorException & { error: { detail: StructuredValidationDetail } } => {
  if (!(error instanceof APIErrorException)) return false;
  const detail = error.error?.detail as StructuredValidationDetail | undefined;
  return (
    !!detail &&
    typeof detail === 'object' &&
    Array.isArray(detail.errors) &&
    typeof detail.message === 'string'
  );
};

/**
 * API error exception class
 * Wraps HTTP errors with typed error data
 */
export class APIErrorException extends Error {
  public readonly status: number;
  public readonly error: APIError;

  constructor(status: number, error: APIError) {
    super(error.message);
    this.name = 'APIErrorException';
    this.status = status;
    this.error = error;

    // Maintain proper stack trace for where error was thrown (V8 engines)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, APIErrorException);
    }
  }

  /**
   * Check if error is a specific HTTP status code
   */
  isStatus(code: number): boolean {
    return this.status === code;
  }

  /**
   * Check if error is a validation error (400)
   *
   * Deliberately excludes 422. `handleMutationError` gates form-field mapping on
   * this, and a 422 body is not a field map — widening it would push unrelated
   * errors into the v3 connector forms. Use `isUnprocessableEntity` for 422.
   */
  isValidationError(): boolean {
    return this.status === 400;
  }

  /**
   * Check if error is an unprocessable entity error (422)
   */
  isUnprocessableEntity(): boolean {
    return this.status === 422;
  }

  /**
   * Check if error is a not found error (404)
   */
  isNotFoundError(): boolean {
    return this.status === 404;
  }

  /**
   * Check if error is a conflict error (409)
   */
  isConflictError(): boolean {
    return this.status === 409;
  }

  /**
   * Check if error is a server error (500+)
   */
  isServerError(): boolean {
    return this.status >= 500;
  }

  /**
   * Get error details if available
   */
  getDetails(): Record<string, any> | undefined {
    return this.error.details;
  }
}
/**
 * react-query retry callback: skip retries on 404 (an unknown / wrong-org
 * resource won't materialise on retry); cap remaining retries at 2.
 */
export const skipRetryOn404 = (failureCount: number, error: unknown): boolean => {
  if (error instanceof APIErrorException && error.status === 404) {
    return false;
  }
  return failureCount < 2;
};
